import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { getPhysicalInventories } from '../../services/physicalInventoryService';
import { getWarehouses } from '../../services/warehouseService';

const PhysicalInventoryList = () => {
  const [inventories, setInventories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    warehouse_id: ''
  });

  // Load warehouses on initial render
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const data = await getWarehouses();
        setWarehouses(data);
      } catch (err) {
        console.error('Error fetching warehouses:', err);
        setError('Failed to load warehouses. Please try again.');
      }
    };
    
    fetchWarehouses();
  }, []);

  // Load inventories with filters
  useEffect(() => {
    const fetchInventories = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const queryParams = {
          page: page + 1,
          limit: rowsPerPage,
          ...filters
        };
        
        // Remove empty filters
        Object.keys(queryParams).forEach(key => {
          if (!queryParams[key]) delete queryParams[key];
        });
        
        const data = await getPhysicalInventories(queryParams);
        
        setInventories(data.items || []);
        setTotalItems(data.total || 0);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching inventories:', err);
        setError('Failed to load inventory data. Please try again.');
        setLoading(false);
      }
    };
    
    fetchInventories();
  }, [page, rowsPerPage, filters]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(0);
  };

  const handleRefresh = () => {
    // Re-fetch data with current filters
    const fetchInventories = async () => {
      try {
        setLoading(true);
        
        const queryParams = {
          page: page + 1,
          limit: rowsPerPage,
          ...filters
        };
        
        // Remove empty filters
        Object.keys(queryParams).forEach(key => {
          if (!queryParams[key]) delete queryParams[key];
        });
        
        const data = await getPhysicalInventories(queryParams);
        
        setInventories(data.items || []);
        setTotalItems(data.total || 0);
        setLoading(false);
      } catch (err) {
        console.error('Error refreshing inventories:', err);
        setError('Failed to refresh inventory data. Please try again.');
        setLoading(false);
      }
    };
    
    fetchInventories();
  };

  if (loading && inventories.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Physical Inventory Counts
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            component={RouterLink}
            to="/inventory"
            sx={{ mr: 1 }}
          >
            Back to Inventory
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/inventory/physical"
          >
            New Count
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              label="Status"
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="warehouse-filter-label">Warehouse</InputLabel>
            <Select
              labelId="warehouse-filter-label"
              name="warehouse_id"
              value={filters.warehouse_id}
              onChange={handleFilterChange}
              label="Warehouse"
            >
              <MenuItem value="">All Warehouses</MenuItem>
              {warehouses.map(warehouse => (
                <MenuItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Inventory List */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Warehouse</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell>User</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No inventory counts found
                  </TableCell>
                </TableRow>
              ) : (
                inventories.map((inventory) => (
                  <TableRow key={inventory.id}>
                    <TableCell>{inventory.id}</TableCell>
                    <TableCell>{inventory.warehouse?.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={inventory.status === 'in_progress' ? 'In Progress' : 'Completed'}
                        color={inventory.status === 'in_progress' ? 'primary' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(inventory.started_at).toLocaleString()}</TableCell>
                    <TableCell>
                      {inventory.completed_at ? new Date(inventory.completed_at).toLocaleString() : '-'}
                    </TableCell>
                    <TableCell>{inventory.user?.username}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={inventory.status === 'in_progress' ? 'Continue Count' : 'View Report'}>
                        <IconButton
                          color="primary"
                          component={RouterLink}
                          to={inventory.status === 'in_progress' 
                            ? `/inventory/physical/${inventory.id}` 
                            : `/inventory/physical/${inventory.id}/discrepancies`}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Container>
  );
};

export default PhysicalInventoryList;
